import {
  IAMUserState,
  JsonSchema,
  JsonSchemaItemProperty,
  JsonSchemaPropertyMap,
  JsonSchemaItems,
  RowData,
  RowDataValueType,
} from "../../../../types";
import { getStoreOptionByGroupId } from "../group-id/groupIdHelpers";
import { formatExcelValue } from "../importCOA/formatExcelValue";
import { getInputType } from "../schemaHelpers";
import { getCompanyIdByStoreId } from "../storeHelpers";
import { getSelectedGroupIdOptionForImport } from "./importOptionHelpers";
import {
  getAllFieldProperties,
  getFieldTitles,
  getIdentifierFields,
} from "./schemaHelpers";

// format excel cell values for import
const formatExcelCellValue = (
  value: RowDataValueType,
  property?: JsonSchemaItemProperty,
): RowDataValueType => {
  const propertyType = property?.type ? getInputType(property.type) : "string";

  return formatExcelValue(
    value,
    propertyType,
    property?.format,
    property?.items as JsonSchemaItems,
  );
};

// resolves the groupId value->label and adds related store and company values to the row data
const mapGroupIdRowDataFields = (
  key: string,
  row: RowData,
  property: JsonSchemaItemProperty,
  columnTitles: Record<string, string>,
  correlationProperties: JsonSchemaPropertyMap,
  iamUserState: IAMUserState,
): void => {
  const columnKey = columnTitles[key];
  const groupIdLabel = String(row[columnKey] ?? "").trim();

  if (!groupIdLabel) {
    return;
  }

  const { value: groupId } = getSelectedGroupIdOptionForImport(
    groupIdLabel,
    iamUserState.eisGroups.groups,
  );
  if (!groupId) {
    return;
  }
  row[columnKey] = groupId;
  const { storeKey } = property;
  if (!storeKey) {
    return;
  }
  const { value: storeId } = getStoreOptionByGroupId({
    selectedGroupId: groupId,
    iamUserState,
  });
  row[columnTitles[storeKey]] = storeId;
  const companyKey = correlationProperties[storeKey]?.companyKey;
  if (!companyKey) {
    return;
  }
  row[columnTitles[companyKey]] = getCompanyIdByStoreId(
    storeId,
    iamUserState.extendedAccountInfo,
  );
};

const mapRow = (
  row: RowData,
  schema: JsonSchema,
  correlationProperties: JsonSchemaPropertyMap,
  columnTitles: Record<string, string>,
  iamUserState: IAMUserState,
): RowData => {
  const mappedRow: RowData = {};

  Object.entries(correlationProperties).forEach(([key, property]) => {
    if (property?.isGroupId) {
      mapGroupIdRowDataFields(
        key,
        row,
        property,
        columnTitles,
        correlationProperties,
        iamUserState,
      );
    }

    mappedRow[key] = formatExcelCellValue(row[columnTitles[key]], property);
  });

  getIdentifierFields(schema).forEach((key) => {
    mappedRow[key] = formatExcelCellValue(
      row[columnTitles[key]],
      schema.properties[key],
    );
  });

  return mappedRow;
};

export const mapExcelRows = (
  rows: RowData[],
  schema: JsonSchema,
  correlationProperties: JsonSchemaPropertyMap,
  iamUserState: IAMUserState,
): RowData[] => {
  const properties = getAllFieldProperties(schema, correlationProperties);
  const columnTitles = getFieldTitles(properties, true);

  return rows.map((row) =>
    mapRow(row, schema, correlationProperties, columnTitles, iamUserState),
  );
};
